import React, { useEffect, useState } from 'react'

import FormBlock from 'components/Forms/FormBlock'
import FormModal from 'components/Modal/FormModal/FormModal'
import Toast, { TOAST_TYPE } from 'components/Toast/Toast'
import Input from 'components/Forms/Input/Input'
import Checkbox from 'components/Forms/Checkbox/Checkbox'

import { RUSSIAN_CURRENCY } from 'constants/currencies'

import { fieldsValidator, getIsIntStringRule, getIsNotEmptyStringRule, getIsValueRule } from 'utils/fieldsValidator'
import { removeKeyFromObject } from 'utils/common'
import { getMonthlyPrice, getYearlyFormattedPrice } from 'utils/payments'

import styles from './UpdatePriceModal.module.scss'

const PriceNote = ({ price, currency, billingCycle }) => {
    if (billingCycle !== 12) return null

    return (
        <div className={styles.priceNote}>
            <span className={styles.price}>{getYearlyFormattedPrice(price, currency, 1)}</span> / year
        </div>
    )
}

const UpdatePriceModal = ({ currenciesSymbol, payload: { product, currency }, onSubmit, onClose }) => {
    const [isNeedToConfirm] = useState(currency !== RUSSIAN_CURRENCY)
    const [initialPrice, setInitialPrice] = useState('')
    const [newPrice, setNewPrice] = useState('')
    const [isConfirm, setIsConfirm] = useState(false)

    const [errors, setErrors] = useState({})

    useEffect(() => {
        setInitialPrice(getMonthlyPrice(product.multiCurrencyPrice[currency], product.billingCycleValue))
    }, [])

    useEffect(() => {
        setNewPrice(initialPrice)
    }, [initialPrice])

    const handleSubmit = async () => {
        try {
            setErrors({})

            const validator = new fieldsValidator()

            validator.addFields([
                validator.createField('newPrice', String(newPrice), [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(),
                    getIsIntStringRule(),
                ]),
            ])

            const { isHasErrors, errors } = validator.validate()
            if (isHasErrors) {
                setErrors(errors)
                return
            }

            const price = product.billingCycleValue === 12 ? newPrice * 12 : newPrice
            await onSubmit({ product, currency, price, isInflated: false })

            return true
        } catch (err) {
            console.error(err)
            Toast(TOAST_TYPE.ERROR)
            throw err
        }
    }

    return (
        <FormModal
            headText="Update Price"
            subHeadText={`${product.name} (${product.productCode})`}
            successText="Price updated successfully!"
            warningText={isNeedToConfirm ? "Don't forget sync price with Stripe!" : ''}
            actionText="Update"
            onAction={() => handleSubmit()}
            onClose={() => onClose()}
            isDisabledAction={String(newPrice) === String(initialPrice) || (isNeedToConfirm && !isConfirm)}
        >
            <FormBlock label={`Old Price (${currenciesSymbol[currency]} / Month)`}>
                <PriceNote price={initialPrice} billingCycle={product.billingCycleValue} currency={currency} />
                <Input value={initialPrice} isDisabled isReadOnly />
            </FormBlock>
            <FormBlock label={`New Price (${currenciesSymbol[currency]} / Month)`}>
                <PriceNote price={newPrice} billingCycle={product.billingCycleValue} currency={currency} />
                <Input
                    value={newPrice}
                    type="number"
                    min={1}
                    error={errors.newPrice}
                    onChange={value => {
                        setNewPrice(value)
                        setErrors(removeKeyFromObject(errors, 'newPrice'))
                    }}
                />
            </FormBlock>
            {isNeedToConfirm && (
                <FormBlock>
                    <Checkbox
                        value={isConfirm}
                        label="I confirm that I am sober and this price is synced with Stripe"
                        onChange={value => setIsConfirm(value)}
                    />
                </FormBlock>
            )}
        </FormModal>
    )
}

export default UpdatePriceModal
